<template>
  <div class="workpackage-settings">
    <!-- Settings trigger button -->
    <tooltip :value="$t('workpackageSettings.title')" position="bottom">
      <v-btn icon class="settings-trigger-btn" @click="openModal">
        <workpackage-settings-icon />
      </v-btn>
    </tooltip>

    <!-- Settings modal -->
    <v-dialog v-model="isOpen" persistent width="1200px">
      <v-card class="workpackage-settings-modal">
        <v-card-title class="modal-header">
          <span class="text-h5 font-weight-bold">{{ $t('workpackageSettings.title') }}</span>
          <v-spacer />
          <v-btn icon @click="closeModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider class="mx-3" />

        <v-card-text class="modal-body">
          <div class="sections-row">
            <!-- Data Refresh History section -->
            <section class="settings-section" aria-labelledby="data-refresh-history-heading">
              <h3 id="data-refresh-history-heading" class="section-heading">
                {{ $t('workpackageSettings.sections.dataRefreshHistory') }}
              </h3>
              <div
                v-if="groupedDataRefreshHistory && Object.keys(groupedDataRefreshHistory).length"
                class="history-list"
              >
                <div
                  v-for="(runs, date) in groupedDataRefreshHistory"
                  :key="date"
                  class="history-group"
                >
                  <span class="history-date">{{ date }}</span>
                  <div v-for="run in runs" :key="run._id" class="history-item">
                    <span class="history-time">{{ formatTime(run.date) }}</span>
                    <span class="history-separator" aria-hidden="true" />
                    <span class="history-description">{{
                      $t('workpackageSettings.dataRefreshRanBy', { username: run.username })
                    }}</span>
                  </div>
                </div>
              </div>
              <p v-else class="empty-section-text">{{ $t('response.noData') }}</p>
            </section>

            <v-divider vertical class="section-divider" />

            <!-- Engine Run History section -->
            <section class="settings-section" aria-labelledby="engine-run-history-heading">
              <h3 id="engine-run-history-heading" class="section-heading">
                {{ $t('workpackageSettings.sections.engineRunHistory') }}
              </h3>
              <div
                v-if="groupedEngineRunHistory && Object.keys(groupedEngineRunHistory).length"
                class="history-list"
              >
                <div
                  v-for="(runs, date) in groupedEngineRunHistory"
                  :key="date"
                  class="history-group"
                >
                  <span class="history-date">{{ date }}</span>
                  <div v-for="run in runs" :key="run._id" class="history-item">
                    <span class="history-time">{{ formatTime(run.date) }}</span>
                    <span class="history-separator" aria-hidden="true" />
                    <span class="history-description"
                      >{{ getEngineRunEventLabel(run) }}
                      {{ $t('workpackageSettings.ranBy', { username: run.username }) }}</span
                    >
                  </div>
                </div>
              </div>
              <p v-else class="empty-section-text">{{ $t('response.noData') }}</p>
            </section>

            <v-divider vertical class="section-divider" />

            <!-- Workpackage Actions section -->
            <section class="settings-section" aria-labelledby="workpackage-actions-heading">
              <h3 id="workpackage-actions-heading" class="section-heading">
                {{ $t('workpackageSettings.sections.workpackageActions') }}
              </h3>

              <div class="actions-container">
                <feature-toggle :toggle="enableCompetitorSimulation">
                  <div class="cost-thresholds-section">
                    <!-- Display section with edit icon -->
                    <div v-if="!isCostThresholdsEditMode" class="cost-thresholds-display">
                      <div class="display-content">
                        <span>
                          {{ $t('workPackages.costChangeThresholds.label') }}:
                          {{ costThresholdsDisplayValue }}
                        </span>
                      </div>
                      <v-icon
                        class="edit-icon"
                        color="primary"
                        size="1.5rem"
                        @click="toggleCostThresholdsEditMode"
                      >
                        mdi-pencil
                      </v-icon>
                    </div>

                    <!-- Edit section with inputs -->
                    <div v-else class="cost-thresholds-edit">
                      <cost-threshold-inputs :workpackage="workpackageForDialog" />
                      <div class="edit-actions">
                        <v-btn
                          x-small
                          outlined
                          class="cancel-edit-btn"
                          @click="toggleCostThresholdsEditMode"
                        >
                          {{ $t('actions.cancel') }}
                        </v-btn>
                      </div>
                    </div>
                  </div>
                </feature-toggle>

                <feature-toggle :toggle="enableRecalculateAllKPIs">
                  <v-btn
                    outlined
                    :disabled="isRecalculateDisabled"
                    :loading="recalculatingAll"
                    class="action-btn"
                    @click="recalculateKpis"
                  >
                    {{ $t('workpackageSettings.actions.recalculateKpis') }}
                  </v-btn>
                </feature-toggle>
                <!-- Rerun price validations button -->
                <v-tooltip v-if="isPriceValidationsEnabled && isAdminLoggedIn" bottom>
                  <template #activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      <v-btn
                        outlined
                        :disabled="isRerunValidationsDisabled"
                        :loading="rerunningPriceValidations"
                        class="action-btn"
                        @click="onRerunPriceValidations"
                      >
                        {{ $t('workpackageSettings.actions.rerunValidations') }}
                      </v-btn>
                    </span>
                  </template>
                  {{ rerunValidationsTooltip }}
                </v-tooltip>
                <!-- Lock all price lists toggle -->
                <div v-if="isLockAllTsgsVisible" class="lock-all-tsgs-row">
                  <v-switch
                    :input-value="areAllTsgsLocked"
                    :disabled="isLockAllTsgsDisabled"
                    :loading="isLockAllLoading"
                    dense
                    hide-details
                    class="lock-all-switch mt-0"
                    @change="onLockAllTsgs"
                  >
                    <template #label>
                      <span class="lock-all-label">
                        <v-icon small class="mr-1">mdi-lock</v-icon>
                        {{ $t('workpackageSettings.actions.lockAllTsgs') }}
                      </span>
                    </template>
                  </v-switch>
                </div>
              </div>
            </section>
          </div>
        </v-card-text>

        <v-divider class="mx-3" />

        <v-card-actions class="modal-footer">
          <v-spacer />
          <v-btn outlined class="close-btn" @click="closeModal">
            {{ $t('actions.close') }}
          </v-btn>
          <feature-toggle :toggle="enableCompetitorSimulation">
            <v-btn
              :disabled="hasCostThresholdValidationError || isSaving || !hasCostThresholdChanges"
              :loading="isSaving"
              class="save-changes-btn"
              @click="saveThresholds"
            >
              {{ $t('workpackageSettings.actions.saveChanges') }}
            </v-btn>
          </feature-toggle>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState, mapGetters } from 'vuex';
import moment from 'moment';
import { get, isNil, cloneDeep } from 'lodash';
import {
  enableCompetitorSimulation,
  enableRecalculateAllKPIs,
  allowStoreGroupPriceLocking,
} from '@enums/feature-flags';
import { dayMonthYearFormat } from '@enums/date-formats';
import { jobStatuses } from '@enums/jobapi';
import numberFormats from '@enums/number-formats';
import featureFlagsMixin from '@/js/mixins/featureFlags';
import formatNumberMixin from '@/js/mixins/formatNumber';
import CostThresholdInputs from './cost-threshold-inputs.vue';
import WorkpackageSettingsIcon from './workpackage-settings-icon.vue';
import { getTsgKeyFromAuditEntry, isAuditLogFlagTrue } from '@/js/utils/audit-log';

export default {
  name: 'WorkpackageSettingsModal',

  components: {
    CostThresholdInputs,
    WorkpackageSettingsIcon,
  },

  mixins: [featureFlagsMixin, formatNumberMixin],

  props: {
    workpackage: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      isOpen: false,
      isSaving: false,
      isCostThresholdsEditMode: false,
      workpackageForDialog: {
        costChangeThresholds: {
          min: null,
          max: null,
        },
      },
      enableCompetitorSimulation,
      enableRecalculateAllKPIs,
      allowStoreGroupPriceLocking,
      isLockAllLoading: false,
      engineRunEventMap: {
        kpiRecalculate: 'workpackageSettings.engineRunTypes.kpiRecalculate',
        'generic:engine-triggered': 'workpackageSettings.engineRunTypes.strategicEngine',
        'generic:optimisation-triggered':
          'workpackageSettings.engineRunTypes.crossElasticityOptimization',
      },
    };
  },

  computed: {
    ...mapState('gridView', [
      'calculatingScenarioResults',
      'recalculatingAll',
      'dataRefreshHistory',
      'engineRunHistory',
    ]),
    ...mapState('workpackages', [
      'selectedWorkpackage',
      'selectedWorkpackageToolStoreGroups',
      'rerunningPriceValidations',
    ]),
    ...mapGetters('context', ['isWholesaleManager', 'isWholesaleAnalyst', 'isAdminLoggedIn']),
    ...mapGetters('scenarioMetadata', ['lockedPricesByTsgKey']),

    /** Indexes tool store groups by their key for fast UUID lookups. */
    toolStoreGroupsByKey() {
      return (this.selectedWorkpackageToolStoreGroups || []).reduce((acc, tsg) => {
        acc[tsg.key] = tsg.description;
        return acc;
      }, {});
    },

    /** Disables the Recalculate KPIs button while the engine is running or the user lacks permission. */
    isRecalculateDisabled() {
      return this.isPermissionDenied || this.calculatingScenarioResults || this.recalculatingAll;
    },

    /**
     * Disables the rerun validations button while validations are running or the engine is running.
     */
    isRerunValidationsDisabled() {
      return (
        this.calculatingScenarioResults || this.rerunningPriceValidations || this.recalculatingAll
      );
    },

    isPermissionDenied() {
      return (this.isWholesaleManager || this.isWholesaleAnalyst) && !this.isAdminLoggedIn;
    },

    /** Reflects the validation error from the cost-threshold-inputs component. */
    hasCostThresholdValidationError() {
      const min = get(this.workpackageForDialog, 'costChangeThresholds.min', null);
      const max = get(this.workpackageForDialog, 'costChangeThresholds.max', null);
      return min !== null && max !== null && min > max;
    },

    /** Formats the cost thresholds for display. */
    costThresholdsDisplayValue() {
      const thresholds = get(this.workpackageForDialog, 'costChangeThresholds', {});
      const min = isNil(thresholds.min) ? 0 : thresholds.min;
      const max = isNil(thresholds.max) ? 0 : thresholds.max;
      return this.formatCostThresholds(min, max);
    },

    /** True when the dialog values differ from the saved workpackage. */
    hasCostThresholdChanges() {
      const original = get(this.workpackage, 'costChangeThresholds', {});
      const originalMin = isNil(original.min) ? 0 : original.min;
      const originalMax = isNil(original.max) ? 0 : original.max;
      const current = get(this.workpackageForDialog, 'costChangeThresholds', {});
      const currentMin = isNil(current.min) ? 0 : current.min;
      const currentMax = isNil(current.max) ? 0 : current.max;
      return currentMin !== originalMin || currentMax !== originalMax;
    },

    /** Groups data refresh history by date. */
    groupedDataRefreshHistory() {
      return this.groupHistoryByDate(this.dataRefreshHistory);
    },

    /** Groups engine run history by date. */
    groupedEngineRunHistory() {
      return this.groupHistoryByDate(this.engineRunHistory);
    },

    isPriceValidationsEnabled() {
      return get(this.$store.state, 'clientConfig.priceValidations.enabled', false);
    },

    /** True when the lock all toggle should be visible — requires the feature flag and at least one TSG. */
    isLockAllTsgsVisible() {
      return (
        this.isFeatureFlagEnabled(allowStoreGroupPriceLocking) &&
        this.selectedWorkpackageToolStoreGroups.length > 0
      );
    },

    /** True when every TSG in the workpackage has prices locked for the current scenario. */
    areAllTsgsLocked() {
      const tsgKeys = this.selectedWorkpackageToolStoreGroups.map(tsg => tsg.key);
      if (!tsgKeys.length) return false;
      return tsgKeys.every(key => this.lockedPricesByTsgKey[key]);
    },

    /** Disables the lock-all toggle while an engine run or lock-all operation is in progress. */
    isLockAllTsgsDisabled() {
      return this.calculatingScenarioResults || this.recalculatingAll || this.isLockAllLoading;
    },

    rerunValidationsTooltip() {
      if (this.calculatingScenarioResults || this.recalculatingAll) {
        return this.$t('workpackageSettings.tooltips.engineRunning');
      }
      if (this.rerunningPriceValidations) {
        return this.$t('workpackageSettings.tooltips.rerunningValidations');
      }
      return this.$t('workpackageSettings.tooltips.rerunValidations');
    },
  },

  methods: {
    ...mapActions('workpackages', [
      'updateWorkpackage',
      'runCompetitorPriceSimulation',
      'rerunPriceValidations',
    ]),
    ...mapActions('scenarioMetadata', ['updateAllLockedPrices']),
    ...mapMutations('notifications', ['addNotification']),
    ...mapActions('gridView', [
      'runKPIsForAllScenarios',
      'fetchDataRefreshHistory',
      'fetchEngineRunHistory',
      'checkRecalculatingAll',
    ]),

    /** Opens the settings modal and initialises the workpackage copy for editing. */
    async openModal() {
      this.workpackageForDialog = cloneDeep(this.workpackage);
      this.isCostThresholdsEditMode = false;
      this.isOpen = true;
      // Fetch history and check running status when modal opens
      await Promise.all([
        this.fetchDataRefreshHistory(this.workpackage._id),
        this.fetchEngineRunHistory(this.workpackage._id),
        this.checkRecalculatingAll(),
      ]).catch(error => {
        console.error('Error fetching modal data:', error);
      });
    },

    /** Closes the settings modal. */
    closeModal() {
      this.isOpen = false;
    },

    /** Persists the cost change thresholds and triggers a competitor price simulation. */
    async saveThresholds() {
      this.isSaving = true;
      try {
        const updates = {
          costChangeThresholds: {
            min: isNil(get(this.workpackageForDialog, 'costChangeThresholds.min'))
              ? null
              : get(this.workpackageForDialog, 'costChangeThresholds.min'),
            max: isNil(get(this.workpackageForDialog, 'costChangeThresholds.max'))
              ? null
              : get(this.workpackageForDialog, 'costChangeThresholds.max'),
          },
        };

        await this.updateWorkpackage({
          workpackage: this.workpackage,
          updates,
        });

        this.addNotification({
          id: `cost-change-thresholds-save-success-${Date.now()}`,
          jobStatus: jobStatuses.finished,
          baseTranslation: 'notifications.updateCostChangeThresholds',
        });

        // Reset edit mode after successful save
        this.isCostThresholdsEditMode = false;

        // Trigger competitor price simulation in the background
        this.runCompetitorPriceSimulation({ workpackageId: this.workpackage._id }).catch(error => {
          // Errors are already handled with notifications in the action
          // eslint-disable-next-line no-console
          console.error('Competitor price simulation error:', error);
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error saving cost thresholds:', error);
        this.addNotification({
          id: `cost-change-thresholds-save-failed-${Date.now()}`,
          jobStatus: jobStatuses.failed,
          baseTranslation: 'notifications.updateCostChangeThresholds',
        });
      } finally {
        this.isSaving = false;
      }
    },

    /** Triggers a KPI recalculation for all scenarios. */
    async recalculateKpis() {
      await this.runKPIsForAllScenarios();
    },

    /** Toggles the edit mode for cost thresholds. */
    toggleCostThresholdsEditMode() {
      this.isCostThresholdsEditMode = !this.isCostThresholdsEditMode;
    },

    /** Formats the cost thresholds for display using decimal number format. */
    formatCostThresholds(min, max) {
      const minLabel = this.$t('workPackages.costChangeThresholds.min');
      const maxLabel = this.$t('workPackages.costChangeThresholds.max');
      const minFormatted = this.formatNumber({ number: min, format: numberFormats.decimal });
      const maxFormatted = this.formatNumber({ number: max, format: numberFormats.decimal });
      return `${minLabel}: ${minFormatted}%, ${maxLabel}: ${maxFormatted}%`;
    },

    /** Groups history items by date. */
    groupHistoryByDate(historyItems) {
      if (!Array.isArray(historyItems) || historyItems.length === 0) {
        return {};
      }
      return historyItems.reduce((grouped, item) => {
        const dateKey = moment(item.date).format(dayMonthYearFormat);
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(item);
        return grouped;
      }, {});
    },

    /** Formats date to HH:mm:ss in 24-hour format. */
    formatTime(dateString) {
      return moment(dateString).format('HH:mm:ss');
    },

    /** Gets the translated event type label for an engine run, appending the TSG name when present. */
    getEngineRunEventLabel(run) {
      const isKpiRecalculate = isAuditLogFlagTrue({ auditLogEntry: run, key: 'skipOptimisation' });

      const translationKey = this.engineRunEventMap[
        isKpiRecalculate ? 'kpiRecalculate' : run.fullEventName
      ];
      const label = translationKey ? this.$t(translationKey) : run.fullEventName;

      const tsgKey = getTsgKeyFromAuditEntry(run);
      if (!tsgKey) return label;

      const tsgName = this.toolStoreGroupsByKey[tsgKey] || tsgKey;
      return `${label} ${this.$t('workpackageSettings.forTsg', { tsgName })}`;
    },

    /** Locks or unlocks prices for all TSGs in the currently selected scenario. */
    async onLockAllTsgs(locked) {
      this.isLockAllLoading = true;
      try {
        await this.updateAllLockedPrices({ locked });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error toggling lock for all TSGs:', error);
      } finally {
        this.isLockAllLoading = false;
      }
    },

    async onRerunPriceValidations() {
      await this.rerunPriceValidations({
        workpackageId: this.selectedWorkpackage._id,
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';

.workpackage-settings {
  display: inline-flex;
  align-items: center;
}

.settings-trigger-btn {
  &:hover svg path {
    opacity: 0.7;
  }
}

.workpackage-settings-modal {
  border-left: 6px solid $common-dialog-border-color;
  border-radius: 0;

  .modal-header {
    color: $common-dialog-header-color;
    padding: 1.6rem 2.4rem 1.2rem;
  }

  .modal-body {
    font-size: 1.4rem;
    color: $common-dialog-text-color;
    padding: 0 2.4rem;
    max-height: 70vh;
    overflow-y: auto;
  }

  .modal-footer {
    padding: 1rem 2.4rem 2rem;
    gap: 0.8rem;
  }
}

.settings-section {
  flex: 1;
  padding: 1.6rem 1.6rem;
  min-width: 0;

  .section-heading {
    font-size: 1.4rem;
    font-weight: 600;
    margin-bottom: 0.8rem;
    color: $common-dialog-header-color;
  }

  .empty-section-text {
    font-size: 1.2rem;
    color: $pricing-grey;
    font-style: italic;
    margin-bottom: 0;
  }
}

.sections-row {
  display: flex;
  flex-direction: row;
  align-items: stretch;
}

.section-divider {
  align-self: stretch;
}

.history-list {
  overflow-y: auto;
  max-height: 35vh;
}

.history-group {
  margin-bottom: 1.2rem;

  .history-date {
    display: block;
    font-weight: 700;
    margin-bottom: 0.4rem;
    font-size: 1.3rem;
  }
}

.history-item {
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  margin-bottom: 0.2rem;
  font-size: 1.2rem;

  .history-time {
    white-space: nowrap;
    flex-shrink: 0;
  }

  .history-separator {
    flex: 1;
    min-width: 0.8rem;
    overflow: hidden;
    white-space: nowrap;
    color: $pricing-grey;

    &::after {
      content: '- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -';
    }
  }

  .history-description {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.cost-thresholds-section {
  margin-bottom: 0;

  .cost-thresholds-display {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.8rem;
    background-color: rgba(0, 0, 0, 0.02);
    border-radius: 2px;

    .display-content {
      flex: 1;
      font-size: 1.2rem;
      color: $common-dialog-text-color;
    }

    .edit-icon {
      cursor: pointer;
      transition: opacity 0.2s;
      margin-left: 0.8rem;
      flex-shrink: 0;

      &:hover {
        opacity: 0.7;
      }
    }
  }

  .cost-thresholds-edit {
    padding: 0.8rem 0;
    margin-bottom: 0.8rem;

    .edit-actions {
      display: flex;
      justify-content: flex-end;
      margin-top: 0.8rem;

      .cancel-edit-btn {
        font-size: 1rem;
      }
    }
  }
}

.actions-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.lock-all-tsgs-row {
  padding: 0.4rem 0;
}

.lock-all-switch {
  .lock-all-label {
    display: flex;
    align-items: center;
    font-size: 1.2rem;
    color: $common-dialog-text-color;
  }
}

.action-btn {
  color: $pricing-primary !important;
  border-color: $pricing-primary !important;
  font-size: 1.2rem;
  letter-spacing: normal;
  width: 100%;

  &.v-btn--disabled {
    color: rgba(0, 0, 0, 0.26) !important;
    border-color: rgba(0, 0, 0, 0.26) !important;
  }
}

.refresh-alerts-row {
  margin-top: 0.8rem;
}

.refresh-alerts-btn {
  color: $pricing-primary !important;
  border-color: $pricing-primary !important;
  font-size: 1.2rem;
  letter-spacing: normal;
}

.save-changes-btn {
  background-color: $pricing-primary !important;
  color: $pricing-white !important;
  font-size: 1.2rem;
  letter-spacing: normal;
}

.close-btn {
  color: $pricing-primary !important;
  border-color: $pricing-primary !important;
  font-size: 1.2rem;
  letter-spacing: normal;
}
</style>
